import React, { useState, useEffect } from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { AccessTime as ClockIcon } from "@mui/icons-material";

interface CountdownTimerProps {
  targetDate: Date;
  onComplete?: () => void;
  prefix?: string;
  variant?: "default" | "minimal";
  sx?: SxProps<Theme>;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function CountdownTimer({
  targetDate,
  onComplete,
  prefix = "Opens in:",
  variant = "default",
  sx,
}: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const calculateTimeLeft = (): TimeLeft | null => {
      const difference = new Date(targetDate).getTime() - new Date().getTime();

      if (difference <= 0) {
        if (onComplete) {
          onComplete();
        }
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, onComplete]);

  if (!timeLeft) {
    return null;
  }

  const formatUnit = (value: number, unit: string) => {
    return `${value}${unit}`;
  };

  if (variant === "minimal") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          ...sx,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: "medium", color: "error.main" }}
        >
          {prefix}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, fontFamily: "monospace" }}>
          {timeLeft.days > 0 && (
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "error.main" }}
            >
              {formatUnit(timeLeft.days, "d")}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "error.main" }}
          >
            {formatUnit(timeLeft.hours, "h")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "error.main" }}
          >
            {formatUnit(timeLeft.minutes, "m")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "error.main" }}
          >
            {formatUnit(timeLeft.seconds, "s")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 2,
        backgroundColor: "info.light",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "info.main",
        ...sx,
      }}
    >
      <ClockIcon sx={{ color: "info.dark" }} />
      <Typography
        variant="body2"
        sx={{ fontWeight: "medium", color: "info.dark" }}
      >
        {prefix}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, fontFamily: "monospace" }}>
        {timeLeft.days > 0 && (
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "info.dark" }}
          >
            {formatUnit(timeLeft.days, "d")}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ fontWeight: "bold", color: "info.dark" }}
        >
          {formatUnit(timeLeft.hours, "h")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: "bold", color: "info.dark" }}
        >
          {formatUnit(timeLeft.minutes, "m")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: "bold", color: "error.main" }}
        >
          {formatUnit(timeLeft.seconds, "s")}
        </Typography>
      </Box>
    </Box>
  );
}

export default CountdownTimer;
